import React, { useState, useRef } from 'react';
import { Popover, Table, Typography, Spin } from 'antd';

const { Text } = Typography;

/**
 * MetadataPopover - displays the terminology code/system stored on a question/answer on hover.
 * ponytail: openrp has no live terminology lookup service (unlike the source project's SNOMED/ICD10
 * microservice), so this only renders whatever static terminology metadata is already in the form
 * schema. Upgrade path: wire a `metadata`/`loading`/`error` fetch hook here if a terminology service
 * is ever added.
 */
const MetadataPopover = ({ terminology, children }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const metadata = null;
  const loading = false;
  const error = null;
  const concept = null;

  const hasTerminology = terminology && (terminology.code || terminology.system);
  if (!hasTerminology) return <>{children}</>;

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setVisible(true), 2000);
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  const data = [
    terminology.code && { key: 'code', property: 'Code', value: terminology.code },
    terminology.display && { key: 'display', property: 'Display', value: terminology.display },
    terminology.system && { key: 'system', property: 'System', value: terminology.system },
    terminology.version && { key: 'version', property: 'Version', value: terminology.version },
    (terminology.active !== undefined || concept?.active !== undefined) && { 
      key: 'active', 
      property: 'Active', 
      value: (concept?.active ?? terminology.active) ? 'Yes' : 'No' 
    },
    metadata && typeof metadata === 'object' && { 
      key: 'metadata', 
      property: 'Metadata', 
      value: <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
        {JSON.stringify(metadata, null, 2)}
      </pre>
    }
  ].filter(Boolean);

  if (data.length === 0 && !loading && !error) return <>{children}</>;

  const popoverContent = loading ? <Spin size="small" /> : 
    error ? <Text type="danger">Failed to load metadata</Text> : (
      <Table
        dataSource={data}
        columns={[
          { title: 'Property', dataIndex: 'property', width: 100, render: t => <Text strong>{t}</Text> },
          { title: 'Value', dataIndex: 'value' }
        ]}
        pagination={false}
        size="small"
        bordered
        style={{ maxWidth: 500 }}
      />
    );

  return (
    <Popover
      content={popoverContent}
      title="Terminology Metadata"
      placement="right"
      overlayStyle={{ maxWidth: 550 }}
      open={visible}
    >
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ 
          textDecoration: visible ? 'underline' : 'none',
          textDecorationStyle: 'dashed',
          cursor: 'help'
        }}
      >
        {children}
      </span>
    </Popover>
  );
};

export default MetadataPopover;





